import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import type { PageBusinessCustomer } from '../../api/api-types';
import type { Loadable, Loaded } from '../../guards/ngrx-loader.guard';
import { actions } from './business-customer.actions';

interface Content {
    content: Loadable<PageBusinessCustomer>;
}

const initialState: Content = {
    content: null,
};

export const state = createFeature({
    name: 'businessCustomer',
    reducer: createReducer(
        initialState,

        on(
            actions.loadPageBusinessCustomer,
            (state): Content => ({
                ...state,
                content: null,
            }),
        ),
        on(
            actions.loadPageBusinessCustomerSuccess,
            (state, { content }): Content => ({
                ...state,
                content: content,
            }),
        ),
    ),
    extraSelectors: (baseSelectors) => {
        const selectIsLoadingBusinessCustomer = createSelector(
            baseSelectors.selectContent,
            (businessCustomerContent) => businessCustomerContent === null,
        );

        const selectBuisnessCustomerHighlights = createSelector(
            baseSelectors.selectContent,
            createBusinessCustomerHighlights,
        );

        const selectWarrantyList = createSelector(baseSelectors.selectContent, createWarrantyList);

        const selectLoadedContent = createSelector(
            baseSelectors.selectContent,
            (content) => content as Loaded<PageBusinessCustomer>,
        );

        return {
            selectIsLoadingBusinessCustomer,
            selectBuisnessCustomerHighlights,
            selectWarrantyList,
            selectLoadedContent,
        };
    },
});

function createBusinessCustomerHighlights(content: Loadable<PageBusinessCustomer>) {
    if (!content) return [];

    const highlights: Array<{ headline: string; copy: string; imageUrl: string }> = [];

    if (
        content.businessCenterHighlight1Image &&
        content.businessCenterHighlight1Image.url &&
        content.businessCenterHighlight1Headline &&
        content.businessCenterHighlight1Copy
    ) {
        highlights.push({
            headline: content.businessCenterHighlight1Headline,
            copy: content.businessCenterHighlight1Copy,
            imageUrl: content.businessCenterHighlight1Image.url,
        });
    }
    if (
        content.businessCenterHighlight2Image &&
        content.businessCenterHighlight2Image.url &&
        content.businessCenterHighlight2Headline &&
        content.businessCenterHighlight2Copy
    ) {
        highlights.push({
            headline: content.businessCenterHighlight2Headline,
            copy: content.businessCenterHighlight2Copy,
            imageUrl: content.businessCenterHighlight2Image.url,
        });
    }

    return highlights;
}

function createWarrantyList(content: Loadable<PageBusinessCustomer>) {
    if (!content) return [];

    const waranties: Array<{
        headline: string;
        copy?: string;
        imageUrl: string;
        list: Array<{ text: string }>;
        downloadPdf?: string;
    }> = [];

    if (content.warranty1 && content.warranty1.headline && content.warranty1.image && content.warranty1.image.url) {
        waranties.push({
            headline: content.warranty1.headline,
            copy: content.warranty1.copy ?? undefined,
            imageUrl: content.warranty1.image.url,
            list: content.warranty1.bulletlist,
            downloadPdf: content.warranty1.downloadPdf?.url ?? undefined,
        });
    }
    if (content.warranty2 && content.warranty2.headline && content.warranty2.image && content.warranty2.image.url) {
        waranties.push({
            headline: content.warranty2.headline,
            copy: content.warranty2.copy ?? undefined,
            imageUrl: content.warranty2.image.url,
            list: content.warranty2.bulletlist,
            downloadPdf: content.warranty2.downloadPdf?.url ?? undefined,
        });
    }

    return waranties;
}
